<template>
	<BaseModal ref="modal" :order="1" :title="$t('redirect_to_input').toString()" modal-class="md:w-2/3" @cancel="hide">
		<div class="flex flex-col space-y-4 w-full">
			<div class="items-center">
				<div class="flex justify-between w-full important">
					<BaseCheckbox
						:label="$t('customer')"
						class="mb-0 pt-3 w-1/2"
						v-model="isCustomerChecked"
						:disabled="!customerEmail"
						:title="customerEmail ? `${$t('send_following_documents_to_customer_input')} ${labelsOfDocuments}` : $t('no_customer_e_mail_saved')"
					/>
					<EMailInput class="mb-0 w-1/2 pr-8 pl-5" label="" v-model="customerEmail" v-if="isCustomerChecked" />
					<div v-else></div>
				</div>
				<div class="flex justify-between w-full important">
					<BaseCheckbox
						:label="$t('lawyer')"
						class="mb-0 pt-3 w-1/2"
						v-model="isLawyerChecked"
						:disabled="!lawyerEmail"
						:title="lawyerEmail ? `${$t('send_following_documents_to_lawyer_input')} ${labelsOfDocuments}` : $t('no_lawyer_e_mail_saved')"
					/>
					<EMailInput class="mb-0 w-1/2 pr-8 pl-5" label="" v-model="lawyerEmail" v-if="isLawyerChecked" />
					<div v-else></div>
				</div>

				<div class="flex justify-between w-full important">
					<BaseCheckbox
						:label="$t('insurance')"
						class="mb-0 pt-3 w-1/2"
						v-model="isInsuranceChecked"
						:disabled="!insuranceEmail"
						:title="insuranceEmail ? `${$t('send_following_documents_to_insurance_input')} ${labelsOfDocuments}` : $t('no_insurance_e_mail_saved')"
					/>
					<EMailInput class="mb-0 w-1/2 pr-8 pl-5" label="" v-model="insuranceEmail" v-if="isInsuranceChecked" />
					<div v-else></div>
				</div>

				<div class="flex justify-between w-full important">
					<BaseCheckbox
						align="start"
						class="mb-0 pt-3 justify-start w-1/2"
						style="align-self: start"
						:label="$t('individual_address')"
						v-model="isCustomMailChecked"
					/>
					<div class="w-1/2 pr-8 pl-5" v-if="isCustomMailChecked">
						<div class="flex flex-row" style="justify-items: center">
							<EMailInput class="mb-0 flex flex-grow" label="" v-model="customEmail" />
							<div
								v-if="showAddMailButton"
								class="bg-sp btn block btn-overview-edit flex text-white justify-start mt-1 ml-2"
								style="align-self: start;"
								@click="addMail"
							>
								<font-awesome-icon :icon="['fas', 'plus']"></font-awesome-icon>
							</div>
						</div>
						<div
							v-for="(moreCustomEmail, index) in moreCustomEmails"
							:key="moreCustomEmail.key"
							class="flex flex-row mt-2"
							style="justify-items: center"
						>
							<EMailInput class="mb-0 flex flex-grow" label="" v-model="moreCustomEmails[index]" />
							<div
								v-if="showRemoveMailButton"
								class="bg-sp btn block btn-overview-edit flex text-white justify-start mt-1 ml-2"
								style="align-self: start;"
								@click="removeMail"
							>
								<font-awesome-icon :icon="['fas', 'trash']"></font-awesome-icon>
							</div>
						</div>
					</div>
					<div v-else></div>
				</div>
			</div>

			<div>
				<BaseTextarea :label="$t('message')" v-model="message" />
			</div>

			<div class="flex justify-end mt-4">
				<BaseButton class="btn-blue" @click="send">{{ $t('send') }}</BaseButton>
			</div>
		</div>
	</BaseModal>
</template>

<script>
import BaseCheckbox from '@/components/forms/inputs/BaseCheckbox';
import BaseButton from '@/components/forms/buttons/BaseButton';
import BaseModal from '@/components/modals/BaseModal';
import ModalMixin from '@/mixins/ModalMixin';
import ModalAction from '@/enum/ModalAction';
import PDFRepository from '@/api/repositories/PDFRepository';
import EMailInput from '@/components/forms/inputs/EMailInput';
import BaseTextarea from '@/components/forms/inputs/BaseTextarea';
import TextTemplateRepository from '@/api/repositories/TextTemplateRepository';

export default {
	name: 'ProcessDocumentSentMailToModal',
	components: { BaseTextarea, EMailInput, BaseModal, BaseButton, BaseCheckbox },
	mixins: [ModalMixin],
	data() {
		return {
			documents: null,
			isCustomerChecked: false,
			isLawyerChecked: false,
			isInsuranceChecked: false,
			isCustomMailChecked: false,
			customerEmail: null,
			insuranceEmail: null,
			lawyerEmail: null,
			customEmail: null,
			moreCustomEmails: [],
			message: null,
		};
	},
	computed: {
		process() {
			return this.$store.getters.getDamageCase;
		},
		labelsOfDocuments() {
			return this.documents?.map(document => document.label).join(', ');
		},
		showAddMailButton() {
			if (this.customEmail === null) {
				return false;
			} else {
				return this.customEmail.length > 0;
			}
		},
		showRemoveMailButton() {
			return this.moreCustomEmails.length >= 1;
		},
	},
	methods: {
		async show(documents) {
			this.documents = documents;
			this.customerEmail = this.process?.customer?.email;
			this.insuranceEmail = this.process?.oppositeInsurance?.email;
			this.lawyerEmail = this.process?.lawyerOffice?.email;
			this.message = null;
			return this.$refs['modal'].show();
		},
		clear() {
			this.documents = null;
			this.isCustomerChecked = false;
			this.isLawyerChecked = false;
			this.isInsuranceChecked = false;
			this.isCustomMailChecked = false;

			this.customerEmail = null;
			this.insuranceEmail = null;
			this.lawyerEmail = null;
			this.customEmail = null;
			this.moreCustomEmails = [];
		},
		addMail() {
			if (this.customEmail != null) {
				if (this.moreCustomEmails[this.moreCustomEmails.length - 1] !== '') {
					this.moreCustomEmails.push('');
				}
			}
		},
		removeMail(index) {
			this.moreCustomEmails.splice(index, 1);
		},
		send() {
			const api = new PDFRepository();
			const uuids = this.documents.map(document => document.uuid);

			const send = mail => api.sentDocumentTo(mail, uuids, this.message);
			const promises = [];

			if (this.isCustomerChecked) {
				promises.push(send(this.customerEmail));
			}
			if (this.isLawyerChecked) {
				promises.push(send(this.lawyerEmail));
			}
			if (this.isInsuranceChecked) {
				promises.push(send(this.insuranceEmail));
			}
			if (this.isCustomMailChecked) {
				promises.push(
					send(this.customEmail).then(_ => {
						if (this.moreCustomEmails.length > 0 && this.moreCustomEmails[0] !== '') {
							this.moreCustomEmails.forEach(element => {
								send(element);
							});
						}
					}),
				);
			}

			if (!promises.length) {
				this.$swal(this.$t('general.error').toString(), this.$t('choose_least_one_recipient').toString(), 'error');
			} else {
				Promise.all(promises)
					.then(() => {
						this.$swal(this.$t('general.success').toString(), this.$t('selected_documents_sent_to_email').toString(), 'success').then(() => {
							this.hide(ModalAction.SAVE);
						});
					})
					.catch(e => {
						this.$swal(this.$t('general.error').toString(), this.$t('error_sending_documents').toString(), 'error');
					});
			}
		},
	},
};
</script>

<style scoped></style>
